import React, { Fragment, useEffect, useState } from "react";
import { CardBody, Col, Row, Table } from "reactstrap";
import { Link } from "react-router-dom";

import {
  // Table as ReactTable,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender
} from '@tanstack/react-table';

import { rankItem } from '@tanstack/match-sorter-utils';

import {
  ProductsGlobalFilter,
  CustomersGlobalFilter,
  OrderGlobalFilter,
  ContactsGlobalFilter,
  CompaniesGlobalFilter,
  LeadsGlobalFilter,
  CryptoOrdersGlobalFilter,
  InvoiceListGlobalSearch,
  TicketsListGlobalFilter,
  NFTRankingGlobalFilter,
  TaskListGlobalFilter,
} from "../../Components/Common/GlobalSearchFilter";

// Column Filter
const Filter = ({
  column,
  // table
}) => {
  const columnFilterValue = column.getFilterValue();

  return (
    <>
      <DebouncedInput
        type="text"
        value={(columnFilterValue ?? '')}
        onChange={(event) => column.setFilterValue(event.target.value)}
        placeholder="Search..."
        className="w-36 border shadow rounded"
        list={column.id + 'list'}
      />
      <div className="h-1" />
    </>
  );
};


// Global Filter
const DebouncedInput = ({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [debounce, onChange, value]);

  return (
    <input
      {...props}
      value={value}
      id="search-bar-0"
      className="form-control search"
      onChange={(e) => setValue(e.target.value)}
    />
  );
};
const TableContainer = ({
  columns,
  data,
  isGlobalFilter,
  isProductsFilter,
  isCustomerFilter,
  isOrderFilter,
  isContactsFilter,
  isCompaniesFilter,
  isLeadsFilter,
  isCryptoOrdersFilter,
  isInvoiceListFilter,
  isTicketsListFilter,
  isNFTRankingFilter,
  isTaskListFilter,
  customPageSize,
  tableClass,
  theadClass,
  trClass,
  thClass,
  divClass,
  SearchPlaceholder,
  onRowClick,

  // Server-side pagination
  manualPagination = false,
  pageCount = -1,
  pageIndex = 0,
  pageSize = 25,
  onPageChange,
  totalRows,

}) => {
  const [columnFilters, setColumnFilters] = useState ([]);
  const [globalFilter, setGlobalFilter] = useState('');

  const fuzzyFilter = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({
      itemRank
    });
    return itemRank.passed;
  };

  const table = useReactTable({
    columns,
    data,

    filterFns: {
      fuzzy: fuzzyFilter,
    },

    state: {
      columnFilters,
      globalFilter,

      ...(manualPagination
        ? {
          pagination: {
            pageIndex,
            pageSize,
          },
        }
        : {}),
    },

    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,

    globalFilterFn: fuzzyFilter,

    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),

    ...(manualPagination
      ? {}
      : {
        getPaginationRowModel: getPaginationRowModel(),
      }),

    getSortedRowModel: getSortedRowModel(),

    manualPagination,

    pageCount: manualPagination ? pageCount : undefined,

    onPaginationChange: manualPagination
      ? (updater) => {
        const current = {
          pageIndex,
          pageSize,
        };

        const next =
          typeof updater === "function"
            ? updater(current)
            : updater;

        if (next.pageIndex !== current.pageIndex) {
          onPageChange?.(next.pageIndex);
        }
      }
      : undefined,
  });

  const {
    getHeaderGroups,
    getRowModel,
    getCanPreviousPage,
    getCanNextPage,
    getPageOptions,
    setPageIndex,
    nextPage,
    previousPage,
    setPageSize,
    getState
  } = table;

  useEffect(() => {
    (customPageSize) && setPageSize((customPageSize));
  }, [customPageSize, setPageSize]);

  return (
    <Fragment>
      {isGlobalFilter && <Row className="mb-3">
        <CardBody className="border border-dashed border-end-0 border-start-0">
          <form>
            <Row>
              <Col sm={5}>
                <div className={(isProductsFilter || isContactsFilter || isCompaniesFilter || isNFTRankingFilter) ? "search-box me-2 mb-2 d-inline-block" : "search-box me-2 mb-2 d-inline-block col-12"}>
                  <DebouncedInput
                    value={globalFilter ?? ''}
                    onChange={value => setGlobalFilter((value))}
                    placeholder={SearchPlaceholder}
                  />
                  <i className="bx bx-search-alt search-icon"></i>
                </div>
              </Col>
              {isProductsFilter && (
                <ProductsGlobalFilter />
              )}
              {isCustomerFilter && (
                <CustomersGlobalFilter />
              )}
              {isOrderFilter && (
                <OrderGlobalFilter />
              )}
              {isContactsFilter && (
                <ContactsGlobalFilter />
              )}
              {isCompaniesFilter && (
                <CompaniesGlobalFilter />
              )}
              {isLeadsFilter && (
                <LeadsGlobalFilter />
              )}
              {isCryptoOrdersFilter && (
                <CryptoOrdersGlobalFilter />
              )}
              {isInvoiceListFilter && (
                <InvoiceListGlobalSearch />
              )}
              {isTicketsListFilter && (
                <TicketsListGlobalFilter />
              )}
              {isNFTRankingFilter && (
                <NFTRankingGlobalFilter />
              )}
              {isTaskListFilter && (
                <TaskListGlobalFilter />
              )}
            </Row>
          </form>
        </CardBody>
      </Row>}


      <div className={divClass}>
        <Table hover className={tableClass}>
          <thead className={theadClass}>
            {getHeaderGroups().map((headerGroup) => (
              <tr className={trClass} key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} className={thClass}  {...{
                    onClick: header.column.getToggleSortingHandler(),
                  }}>
                    {header.isPlaceholder ? null : (
                      <React.Fragment>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          asc: ' ',
                          desc: ' ',
                        }
                        [header.column.getIsSorted()] ?? null}
                        {header.column.getCanFilter() ? (
                          <div>
                            <Filter column={header.column} table={table} />
                          </div>
                        ) : null}
                      </React.Fragment>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {getRowModel().rows.map((row) => {
              return (
                <tr
                  key={row.id}
                  onClick={() => onRowClick && onRowClick(row.original)}
                  style={onRowClick ? { cursor: "pointer" } : undefined}
                >
                  {row.getVisibleCells().map((cell) => {
                    return (
                      <td key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>

      <Row className="align-items-center mt-2 g-3 text-center text-sm-start">

        <div className="col-sm">
          <div className="text-muted">
            Showing{" "}
            <span className="fw-semibold">
              {manualPagination
                ? data.length
                : getState().pagination.pageSize}
            </span>
            {" "}of{" "}
            <span className="fw-semibold">
              {manualPagination
                ? totalRows ?? data.length
                : data.length}
            </span>
            {" "}Results
          </div>
        </div>

        <div className="col-sm-auto">
          <ul className="pagination pagination-separated pagination-md justify-content-center justify-content-sm-start mb-0">

            {/* Previous */}
            <li
              className={
                manualPagination
                  ? pageIndex <= 0
                    ? "page-item disabled"
                    : "page-item"
                  : !getCanPreviousPage()
                    ? "page-item disabled"
                    : "page-item"
              }
            >
              <Link
                to="#"
                className="page-link"
                onClick={(e) => {
                  e.preventDefault();

                  if (manualPagination) {
                    if (pageIndex > 0) {
                      onPageChange?.(pageIndex - 1);
                    }
                  } else {
                    previousPage();
                  }
                }}
              >
                Previous
              </Link>
            </li>

            {/* Pages */}
            {manualPagination
              ? Array.from(
                {
                  length: Math.max(
                    1,
                    Math.ceil(
                      (totalRows ?? data.length) / pageSize
                    )
                  ),
                },
                (_, index) => index
              ).map((page) => (
                <li className="page-item" key={page}>
                  <Link
                    to="#"
                    className={
                      pageIndex === page
                        ? "page-link active"
                        : "page-link"
                    }
                    onClick={(e) => {
                      e.preventDefault();

                      if (page !== pageIndex) {
                        onPageChange?.(page);
                      }
                    }}
                  >
                    {page + 1}
                  </Link>
                </li>
              ))
              : getPageOptions().map((item, key) => (
                <React.Fragment key={key}>
                  <li className="page-item">
                    <Link
                      to="#"
                      className={
                        getState().pagination.pageIndex === item
                          ? "page-link active"
                          : "page-link"
                      }
                      onClick={(e) => {
                        e.preventDefault();
                        setPageIndex(item);
                      }}
                    >
                      {item + 1}
                    </Link>
                  </li>
                </React.Fragment>
              ))}

            {/* Next */}
            <li
              className={
                manualPagination
                  ? pageIndex >=
                    Math.ceil(
                      (totalRows ?? data.length) / pageSize
                    ) - 1
                    ? "page-item disabled"
                    : "page-item"
                  : !getCanNextPage()
                    ? "page-item disabled"
                    : "page-item"
              }
            >
              <Link
                to="#"
                className="page-link"
                onClick={(e) => {
                  e.preventDefault();

                  if (manualPagination) {
                    const totalPages = Math.ceil(
                      (totalRows ?? data.length) / pageSize
                    );

                    if (pageIndex < totalPages - 1) {
                      onPageChange?.(pageIndex + 1);
                    }
                  } else {
                    nextPage();
                  }
                }}
              >
                Next
              </Link>
            </li>

          </ul>
        </div>

      </Row>
    </Fragment>
  );
};

export default TableContainer;